import { z } from 'zod';
import { GongFlowStepActionTypeSchema } from '../schemas/gong-flow';

/**
 * Wire schemas for `/api/flow/gong/flows*` (U9, Gong Flow Play + Queue
 * redesign) — the browse/steps/prefs/suggested API the play picker (U12) and
 * browse surface consume. Snake_case wire keys, `.strict()` where the
 * consumer enumerates every field (behavior specs, Codex) — camelCase
 * Prisma fields (`src/services/flow/gong-flow-catalog.ts`) are mapped onto
 * these at the service boundary, never passed through raw.
 *
 * Schemas are the source of truth (`z.infer` only — `terra/no-parallel-schema-and-type`).
 */

// ── One flow row, shared by browse and suggested ───────────────────────────

/**
 * One flow as rendered by the picker/browse surface. Behavior specs #2/#3/#6/#7
 * (Codex): `flow_id` is a non-empty string and `name` a string (`''` is legal on Gong's wire; never coerced/fabricated);
 * `folder_name` is `null` for Gong's "no folder" flows (the wire's own
 * `folderName` is a required-but-sometimes-empty string — `''` is normalized
 * to `null` here so the picker's "Unfiled" copy has one clean signal);
 * `last_sent_at` is `null` when this rep has never sent this flow in the
 * window, and sorts last; `send_count_30d`/`step_count` are always present,
 * non-negative integers (0, not omitted, when there's nothing to count).
 * `pinned` is required — a hidden flow is OMITTED from the flows array
 * entirely rather than returned with a flag (spec #7).
 */
export const GongFlowSummarySchema = z.object({
  flow_id: z.string().min(1),
  // Display text only (`flow_id` is the key). Gong's own wire `name` is a plain
  // `z.string()` and the mirror stores it verbatim, so `''` is a real value; a
  // `.min(1)` here 500'd the whole catalog on one such row (bd-8ssra.44).
  name: z.string(),
  folder_name: z.string().min(1).nullable(),
  visibility: z.string(),
  creation_date: z.string(),
  send_count_30d: z.number().int().nonnegative(),
  last_sent_at: z.string().nullable(),
  step_count: z.number().int().nonnegative(),
  pinned: z.boolean(),
});
export type GongFlowSummary = z.infer<typeof GongFlowSummarySchema>;

// ── GET /api/flow/gong/flows — browse ───────────────────────────────────────

/**
 * Browse response — the FULL available catalog in one payload, NOT a page
 * (round-5 plan Decision #1: `next_cursor` and the `search`/`folder`/`sort`/
 * `cursor` query params were removed, and filtering/sorting/paging moved
 * client-side, because every keystroke was re-driving a live Gong drain +
 * ~660 mirror writes — QA doc §2. ~664 summaries is tens of KB, far below
 * other Flow payloads). Behavior spec #4/#5: `stale` is required and never
 * nullable (`true` ⇔ every result came from the mirror because live Gong
 * failed); `folders` is the distinct non-empty mirror folder names, with NO
 * "All" sentinel (the browse surface adds its own "All folders" option).
 */
export const GongFlowBrowseResponseSchema = z.object({
  flows: z.array(GongFlowSummarySchema),
  stale: z.boolean(),
  folders: z.array(z.string().min(1)),
});
export type GongFlowBrowseResponse = z.infer<typeof GongFlowBrowseResponseSchema>;

/** `sort=recent` (default) ranks by this rep's own most-recent send; `sort=newest` ranks by Gong's `creationDate`. */
export const GongFlowBrowseSortSchema = z.enum(['recent', 'newest']);
export type GongFlowBrowseSort = z.infer<typeof GongFlowBrowseSortSchema>;

// ── Shared filter/search/sort (server AND client run THIS function) ─────────

export interface GongFlowFilterSortOptions {
  folder?: string | null;
  search?: string;
  sort: GongFlowBrowseSort;
}

/**
 * Whitespace-collapsed, lowercased search term — the one normalization both
 * the token split and the exact/prefix relevance comparisons use, so
 * `"  Intro   Call "` and `"intro call"` behave identically.
 */
function normalizeSearchTerm(search: string): string {
  return search.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Name AND folder (round-5 plan Q2d): `"brandon"` must find a flow filed
 * under "Brandon's plays" even though its name never says "brandon". `'\n'`
 * separates the two so a token can never straddle the boundary.
 */
function searchHaystack(row: GongFlowSummary): string {
  return `${row.name}\n${row.folder_name ?? ''}`.toLowerCase();
}

function matchesAllTokens(haystack: string, tokens: string[]): boolean {
  return tokens.every((token) => haystack.includes(token));
}

/**
 * Relevance tier for one already-matching row (lower sorts first, Q2e): the
 * flow the rep literally typed the name of must be the first row, not
 * whichever match they happened to send most recently. Only meaningful while
 * a search term is present — with no term every row is tier 0 and ordering is
 * byte-identical to the pre-round-5 pinned/recency/name behavior.
 */
function relevanceTier(row: GongFlowSummary, term: string, tokens: string[]): number {
  const name = row.name.trim().replace(/\s+/g, ' ').toLowerCase();
  if (name === term) return 0;
  if (name.startsWith(term)) return 1;
  if (matchesAllTokens(name, tokens)) return 2;
  return 3; // matched only via folder_name (the row is in `result` at all, so it matched something).
}

/**
 * Folder filter (exact match) + token-AND search over name + folder + sort,
 * with pinned flows floated to the top. Pure — no Prisma, no fetch, no React:
 * `browseGongFlows` (server) and `useGongFlowBrowseController` (client, per
 * keystroke over the already-loaded catalog) both call THIS function, so
 * search behavior cannot drift between the two (round-5 plan Decision #2).
 *
 * Search is token-AND, not contiguous-substring (QA doc §2: `"outbound
 * sticker"` missed `"Outbound — 'Sticker' drop"` because of the punctuation
 * between the tokens) — every whitespace-separated token must appear
 * somewhere in `name` or `folder_name`, case-insensitively. With a term
 * present, relevance tier (exact name → name prefix → name contains-all →
 * folder-only) outranks the pinned float and the recency/name tiebreaks; with
 * NO term, ordering is exactly what it was before round 5.
 */
export function filterAndSortGongFlows(
  rows: GongFlowSummary[],
  opts: GongFlowFilterSortOptions,
): GongFlowSummary[] {
  let result = rows;
  if (opts.folder) {
    result = result.filter((r) => r.folder_name === opts.folder);
  }

  const term = opts.search ? normalizeSearchTerm(opts.search) : '';
  const tokens = term.length > 0 ? term.split(' ') : [];
  if (tokens.length > 0) {
    result = result.filter((r) => matchesAllTokens(searchHaystack(r), tokens));
  }

  // Decorate-sort-undecorate: the tier is computed once per row rather than
  // per comparison (and never keyed by `flow_id`, which fixtures may repeat).
  const decorated = result.map((row) => ({
    row,
    tier: tokens.length > 0 ? relevanceTier(row, term, tokens) : 0,
  }));

  decorated.sort((a, b) => {
    if (a.tier !== b.tier) return a.tier - b.tier;
    if (a.row.pinned !== b.row.pinned) return a.row.pinned ? -1 : 1;
    if (opts.sort === 'newest') {
      return b.row.creation_date.localeCompare(a.row.creation_date);
    }
    // 'recent' (default): this rep's own last send, nulls (never sent) last.
    if (a.row.last_sent_at === b.row.last_sent_at) return a.row.name.localeCompare(b.row.name);
    if (a.row.last_sent_at === null) return 1;
    if (b.row.last_sent_at === null) return -1;
    return b.row.last_sent_at.localeCompare(a.row.last_sent_at);
  });

  return decorated.map((d) => d.row);
}

// ── GET /api/flow/gong/flows/suggested — Tier-1 ranking ─────────────────────

/**
 * Suggested-3 response. Behavior spec #1: 0-3 entries, ordered by
 * `send_count_30d` descending, excluding zero-send or hidden flows. `[]` is
 * the intended cold-start render (Spencer 2026-08-19 — tiers 2/3 CUT), never
 * backfilled with a fallback.
 */
export const GongFlowSuggestedResponseSchema = z.object({
  flows: z.array(GongFlowSummarySchema).max(3),
});
export type GongFlowSuggestedResponse = z.infer<typeof GongFlowSuggestedResponseSchema>;

// ── GET /api/flow/gong/flows/:flowId/steps ──────────────────────────────────

/**
 * One step, as rendered by the step-override editor (U13). Behavior spec #8:
 * strict shape, `step_order` 1-based, `step_type` restricted to Gong's six
 * action types, `subject`/`body` are `''` (never `null`/`undefined`) when
 * blank.
 */
export const GongFlowStepDetailWireSchema = z.object({
  step_order: z.number().int().min(1),
  step_type: GongFlowStepActionTypeSchema,
  subject: z.string(),
  body: z.string(),
  /**
   * Gong's "this step replies in the previous step's thread" flag, surfaced so
   * the editor can hide the subject field: Gong REJECTS a subject on a reply
   * step and 400s the ENTIRE enrollment over it, so a rep must not be able to
   * type one in the first place. `.default(false)` keeps this purely additive
   * for any client or cached response predating the field.
   */
  is_reply: z.boolean().default(false),
});
export type GongFlowStepDetailWire = z.infer<typeof GongFlowStepDetailWireSchema>;

// Named `...ApiResponseSchema`, not `...ResponseSchema` — `GongFlowStepsResponseSchema`
// is already taken by Gong's OWN `POST /v2/flows/steps` wire response
// (`src/shared/schemas/gong.ts`), a different shape entirely (that one wraps
// `flows: [{ id, steps }]`, this one is this endpoint's flat `{ steps }`).
// `terra/no-duplicate-schema-name` caught the near-miss.
/**
 * `flow_owner_name` (U13, portable step-override editor's owner note) —
 * `null` today for every real flow: the `gong_flows.owner_name` column has
 * no populating source (`GET /v2/flows` carries no creator/owner field,
 * CONFIRMED 2026-08-18, `docs/flow/gong-flow-play-spec-handoff.md` Check 2;
 * see `core.prisma`'s `GongFlow.ownerName` doc comment). Wired through
 * honestly rather than guessed — CLAUDE.md "No Hardcoded Fallbacks": the
 * client renders the owner-note sentence only when this is non-null, and
 * omits it otherwise, rather than fabricating a name. NOT `.strict()` (see
 * the naming note below) so this is a purely additive field.
 */
export const GongFlowStepsApiResponseSchema = z.object({
  steps: z.array(GongFlowStepDetailWireSchema),
  flow_owner_name: z.string().nullable(),
  /**
   * `true` means Gong was HELD (a quota/rate-limit hold, not a real outage —
   * see `isGongHeld`) when this response was built, and `steps` is the
   * mirror's last-synced copy, which may be out of date. The client decides
   * how to say so (a note in the step-override editor, a follow-up PR — see
   * `getGongFlowSteps`'s doc comment). `.default(false)` keeps this additive
   * for any cached response or client predating the field, same stance as
   * `is_reply` above.
   */
  stale: z.boolean().default(false),
});
export type GongFlowStepsApiResponse = z.infer<typeof GongFlowStepsApiResponseSchema>;

// ── PUT /api/flow/gong/flows/:flowId/preference — pin/hide ──────────────────

/** Write-side enum — `null` clears the preference (idempotent delete). */
export const GongFlowPreferenceStateSchema = z.enum(['pinned', 'hidden']);
export type GongFlowPreferenceState = z.infer<typeof GongFlowPreferenceStateSchema>;

export const GongFlowPreferenceRequestSchema = z.object({
  state: GongFlowPreferenceStateSchema.nullable(),
});
export type GongFlowPreferenceRequest = z.infer<typeof GongFlowPreferenceRequestSchema>;

/** Behavior spec #7: `state: null` in the response confirms preference deletion. */
export const GongFlowPreferenceResponseSchema = z.object({
  flow_id: z.string().min(1),
  state: GongFlowPreferenceStateSchema.nullable(),
});
export type GongFlowPreferenceResponse = z.infer<typeof GongFlowPreferenceResponseSchema>;
